import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ToolShell } from "@/components/tools/ToolShell";
import { getPtBrToolBySlug } from "@/lib/pt-br-tools-catalog";
import { getPtBrToolContent } from "@/lib/pt-br-tool-content";
import { ToolBody } from "../free-tools/$slug";
import { getEsSlugForEnSlug } from "@/lib/es-tools-catalog";
import { getDeSlugForEnSlug } from "@/lib/de-tools-catalog";

export const Route = createFileRoute("/pt-br/$slug")({
  loader: ({ params }) => {
    if (params.slug === "pdf-tools") throw redirect({ to: "/pt-br/pdf-tools", statusCode: 301 });
    if (params.slug === "sobre-o-picamini" || params.slug === "sobre-nos" || params.slug === "about") throw redirect({ to: "/pt-br/about", statusCode: 301 });
    if (params.slug === "contato" || params.slug === "contact") throw redirect({ to: "/pt-br/contact", statusCode: 301 });
    if (params.slug === "politica-de-privacidade" || params.slug === "privacy-policy") throw redirect({ to: "/pt-br/privacy-policy", statusCode: 301 });
    if (params.slug === "termos-de-uso" || params.slug === "terms-of-use") throw redirect({ to: "/pt-br/terms-of-use", statusCode: 301 });
    if (params.slug === "aviso-legal" || params.slug === "disclaimer") throw redirect({ to: "/pt-br/disclaimer", statusCode: 301 });

    const tool = getPtBrToolBySlug(params.slug);
    if (!tool) throw notFound();
    if (tool.slug !== params.slug) {
      throw redirect({ to: "/pt-br/$slug", params: { slug: tool.slug }, statusCode: 301 });
    }
    return { tool };
  },
  head: ({ params }) => {
    const tool = getPtBrToolBySlug(params.slug);
    if (!tool) return { meta: [{ title: "Ferramenta — Picamini.online" }] };

    const content = getPtBrToolContent(tool.slug, tool.name);
    const canonicalUrl = `https://picamini.online/pt-br/${tool.slug}`;
    const enUrl = `https://picamini.online/${tool.enSlug}`;
    const esSlug = getEsSlugForEnSlug(tool.enSlug);
    const esUrl = `https://picamini.online/es/${esSlug}`;
    const deSlug = getDeSlugForEnSlug(tool.enSlug);
    const deUrl = `https://picamini.online/de/${deSlug}`;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${tool.name} - Picamini.online`,
      "url": canonicalUrl,
      "description": content.seoDescription,
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "All",
      "browserRequirements": "Requer JavaScript. Requer HTML5.",
      "inLanguage": "pt-BR",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
      "featureList": content.features || [],
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Início",
          "item": "https://picamini.online/pt-br/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Ferramentas Gratuitas",
          "item": "https://picamini.online/pt-br/",
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": tool.name,
          "item": canonicalUrl,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    return {
      meta: [
        { title: content.seoTitle || `${tool.name} — Picamini.online` },
        { name: "description", content: content.seoDescription },
        { name: "keywords", content: tool.keywords ? tool.keywords.join(", ") : "" },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:locale", content: "pt_BR" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: canonicalUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(webAppSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbSchema),
        },
        ...(faqSchema ? [{
          type: "application/ld+json",
          children: JSON.stringify(faqSchema),
        }] : []),
      ],
    };
  },
  component: PtBrToolPageComponent,
});

function PtBrToolPageComponent() {
  const { tool } = Route.useLoaderData();
  const content = getPtBrToolContent(tool.slug, tool.name);

  return (
    <ToolShell tool={tool} content={content} lang="pt-BR">
      <ToolBody slug={tool.enSlug} />
    </ToolShell>
  );
}
